import { create } from 'zustand';
import { User } from '../types';
import { supabase } from '../lib/supabase';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  emailSent: boolean;
  sentEmail: string | null;
  lastEmailSentAt: number | null;
  login: (email: string) => Promise<void>;
  resendMagicLink: () => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  clearEmailSentState: () => void;
  getSessionToken: () => Promise<string | null>;
  loginWithSessionToken: (token: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: true,
  error: null,
  isAuthenticated: false,
  emailSent: false,
  sentEmail: null,
  lastEmailSentAt: null,

  login: async (email: string) => {
    try {
      set({ isLoading: true, error: null, emailSent: false });

      // Send magic link via Supabase
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          data: {
            email, // Store email in user metadata when they sign up
          },
          emailRedirectTo: `${window.location.origin}/leads`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      // Mark email as sent
      set({
        emailSent: true,
        sentEmail: email,
        lastEmailSentAt: Date.now(),
        isLoading: false,
        error: null,
      });
    } catch (error) {
      set({
        isLoading: false,
        emailSent: false,
        error:
          error instanceof Error
            ? error.message
            : 'An error occurred during login',
      });
    }
  },

  resendMagicLink: async () => {
    const { sentEmail } = get();
    if (!sentEmail) {
      set({ error: 'No email address found to resend to' });
      return;
    }

    try {
      set({ isLoading: true, error: null });

      const { error } = await supabase.auth.signInWithOtp({
        email: sentEmail,
        options: {
          shouldCreateUser: true,
          data: {
            email: sentEmail,
          },
          emailRedirectTo: `${window.location.origin}/leads`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      set({
        lastEmailSentAt: Date.now(),
        isLoading: false,
        error: null,
      });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error
            ? error.message
            : 'An error occurred while resending the magic link',
      });
    }
  },

  clearEmailSentState: () => {
    set({
      emailSent: false,
      sentEmail: null,
      lastEmailSentAt: null,
      error: null,
    });
  },

  logout: async () => {
    try {
      set({ isLoading: true, error: null });

      // Sign out from Supabase
      await supabase.auth.signOut();

      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        emailSent: false,
        sentEmail: null,
        lastEmailSentAt: null,
        error: null,
      });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error
            ? error.message
            : 'An error occurred during logout',
      });
    }
  },

  checkSession: async () => {
    if (!get().isLoading) {
      set({ isLoading: true });
    }

    try {
      // Check for existing Supabase session
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.warn('Error getting session:', error);
      }

      if (session?.user) {
        // User has an existing session
        const user: User = {
          id: session.user.id,
          email: session.user.email || '',
          firstName:
            session.user.user_metadata?.firstName ||
            (session.user.email
              ? session.user.email.split('@')[0].charAt(0).toUpperCase() +
                session.user.email.split('@')[0].slice(1).toLowerCase()
              : ''),
          lastName: session.user.user_metadata?.lastName,
          phoneNumber: session.user.user_metadata?.phoneNumber,
          onboardingCompleted:
            session.user.user_metadata?.onboardingCompleted || false,
          createdAt: session.user.created_at || new Date().toISOString(),
        };

        set({
          user,
          isAuthenticated: !!session.user.email, // Authenticated if they have email
          isLoading: false,
          error: null,
        });
      } else {
        // No session exists, sign in anonymously
        const {
          data: { user: anonUser },
          error: anonError,
        } = await supabase.auth.signInAnonymously();

        if (anonError) {
          console.warn('Error signing in anonymously:', anonError);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          return;
        }

        if (anonUser) {
          const user: User = {
            id: anonUser.id,
            email: '', // Empty for anonymous users
            firstName: '', // Empty for anonymous users
            onboardingCompleted: false,
            createdAt: anonUser.created_at || new Date().toISOString(),
          };

          // Don't identify anonymous users in Clarity
          set({
            user,
            isAuthenticated: false, // Anonymous users are not considered authenticated
            isLoading: false,
            error: null,
          });
        } else {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      }
    } catch (error) {
      console.warn('Error in checkSession:', error);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error:
          error instanceof Error
            ? error.message
            : 'An error occurred checking the session',
      });
    }
  },

  getSessionToken: async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        // Return the full session object as a JSON string
        return JSON.stringify({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          expires_at: session.expires_at,
          user: session.user,
        });
      }
      return null;
    } catch (error) {
      console.error('Error getting session token:', error);
      return null;
    }
  },

  loginWithSessionToken: async (token: string) => {
    try {
      set({ isLoading: true, error: null });

      const sessionData = JSON.parse(token);

      // Set the session using Supabase's setSession method
      const { data, error } = await supabase.auth.setSession({
        access_token: sessionData.access_token,
        refresh_token: sessionData.refresh_token,
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.session?.user) {
        // Create user object from session
        const user: User = {
          id: data.session.user.id,
          email: data.session.user.email || '',
          firstName:
            data.session.user.user_metadata?.firstName ||
            (data.session.user.email
              ? data.session.user.email.split('@')[0].charAt(0).toUpperCase() +
                data.session.user.email.split('@')[0].slice(1).toLowerCase()
              : ''),
          lastName: data.session.user.user_metadata?.lastName,
          phoneNumber: data.session.user.user_metadata?.phoneNumber,
          onboardingCompleted:
            data.session.user.user_metadata?.onboardingCompleted || false,
          createdAt: data.session.user.created_at || new Date().toISOString(),
        };

        set({
          user,
          isAuthenticated: !!data.session.user.email,
          isLoading: false,
          error: null,
          emailSent: false,
          sentEmail: null,
          lastEmailSentAt: null,
        });
      } else {
        throw new Error('Invalid session data');
      }
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error
            ? `Invalid token: ${error.message}`
            : 'Invalid session token format',
      });
    }
  },
}));
